/* eslint-disable boundaries/element-types */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, BugOutlined, HomeOutlined } from "@ant-design/icons";
import { Collapse, Flex } from "antd";
import { createStyles } from "antd-style";

import { isDevelopment } from "@av/build-constants";
import { Button } from "@av/components/atoms/Button";
import { Image } from "@av/components/atoms/Image";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Badge } from "@av/components/molecules/Badge";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

const useStyles = createStyles(({ token, css }) => ({
  errorContainer: css`
    min-height: 100vh;
    text-align: center;
    padding: ${token.paddingLG}px;
  `,

  errorImage: css`
    max-width: 400px;
  `,

  errorTitle: css`
    margin-top: 0;
    margin-bottom: ${token.marginXS}px;
  `,

  devErrorInfo: css`
    width: 100%;
    margin-top: ${token.marginLG}px;
    text-align: left;
  `,

  stackTrace: css`
    background: ${token.colorBgContainer};
    padding: ${token.paddingMD}px;
    border-radius: ${token.borderRadiusLG}px;
    font-family: ${token.fontFamilyCode};
    font-size: 12px;
    line-height: 1.4;
    white-space: pre-wrap;
    overflow-x: auto;
    color: ${token.colorTextSecondary};
  `,

  componentStack: css`
    background: ${token.colorInfoBg};
    padding: ${token.paddingMD}px;
    border-radius: ${token.borderRadiusLG}px;
    font-family: ${token.fontFamilyCode};
    font-size: 12px;
    line-height: 1.4;
    white-space: pre-wrap;
    overflow-x: auto;
    color: ${token.colorInfo};
  `,

  errorMessage: css`
    background: ${token.colorErrorBg};
    padding: ${token.paddingMD}px;
    border-radius: ${token.borderRadiusLG}px;
    color: ${token.colorError};
    font-weight: 500;
    margin-bottom: ${token.marginMD}px;
  `,

  devTips: css`
    color: ${token.colorInfo};
    line-height: ${token.lineHeight};
  `,

  devModeBanner: css`
    background: linear-gradient(135deg, ${token.colorWarning}, ${token.colorWarningBorder});
    color: ${token.colorTextLightSolid};
    padding: ${token.paddingXS}px ${token.paddingMD}px;
    border-radius: ${token.borderRadiusLG}px;
    margin-bottom: ${token.marginMD}px;
    font-weight: 500;
    font-size: ${token.fontSizeSM}px;
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
    box-shadow: ${token.boxShadowTertiary};
  `,

  devModeIcon: css`
    font-size: ${token.fontSizeLG}px;
  `,
}));

type GenericErrorPageProps = {
  showGoBack?: boolean;
  showGoToDashboard?: boolean;
  customActions?: React.ReactNode;
  dashboardPath?: string;
  handleGoToAppDashboard?: () => void;
  onDismissError: () => void;
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
  boundaryName?: string;
};

export function GenericErrorPage({
  showGoBack = true,
  showGoToDashboard = true,
  customActions,
  onDismissError,
  handleGoToAppDashboard,
  error,
  errorInfo,
  boundaryName,
}: GenericErrorPageProps) {
  const { t } = useTranslation();
  const { styles, theme } = useStyles();
  const navigate = useNavigate();

  const errorTitle = t("common.components.errors_generic_title");
  const errorMessage = t("common.components.errors_generic_message");
  const defaultImageAlt = errorTitle;

  useDocumentTitle(errorTitle);

  const formatStackTrace = (stack?: string) => {
    if (!stack) return "No stack trace available";

    return stack
      .split("\n")
      .slice(1, 10)
      .map((line) => line.trim())
      .join("\n");
  };

  const formatComponentStack = (componentStack?: string) => {
    if (!componentStack) return "No component stack available";

    return componentStack
      .split("\n")
      .filter((line) => line.trim())
      .map((line) => line.trim())
      .join("\n");
  };

  const devErrorItems =
    isDevelopment && error
      ? [
          {
            key: "1",
            label: "🔍 Error Details",
            children: (
              <div>
                <div className={styles.errorMessage}>
                  <strong>{error.name || "Error"}:</strong> {error.message || "Unknown error"}
                </div>
                {boundaryName && (
                  <div style={{ marginBottom: theme.marginMD }}>
                    <strong>Boundary:</strong> {boundaryName}
                  </div>
                )}
                <div style={{ marginBottom: theme.marginMD }}>
                  <strong>Timestamp:</strong> {new Date().toLocaleString()}
                </div>
              </div>
            ),
          },
          {
            key: "2",
            label: "📋 Stack Trace",
            children: <div className={styles.stackTrace}>{formatStackTrace(error.stack)}</div>,
          },
          ...(errorInfo?.componentStack
            ? [
                {
                  key: "3",
                  label: "🧩 Component Stack",
                  children: (
                    <div className={styles.componentStack}>
                      {formatComponentStack(errorInfo.componentStack)}
                    </div>
                  ),
                },
              ]
            : []),
          {
            key: "4",
            label: "💡 Development Tips",
            children: (
              <div className={styles.devTips}>
                • Check the component that triggered the error
                <br />
                • Review recent changes in the failing component
                <br />
                • Verify data types and null checks
                <br />
                • Use React DevTools for component inspection
                <br />• Look for undefined variables or missing props
              </div>
            ),
          },
        ]
      : [];

  const handleGoToDashboard = () => {
    navigate("/");
  };

  return (
    <main className={styles.errorContainer}>
      <Flex vertical align="center" justify="center" style={{ minHeight: "100vh" }} gap="large">
        <Image
          testIdPrefix="generic-error"
          src="/errors/error.png"
          alt={defaultImageAlt}
          preview={false}
          className={styles.errorImage}
        />
        <Typography.Title testIdPrefix="generic-error" level={1} className={styles.errorTitle}>
          {errorTitle}
        </Typography.Title>
        <Typography.Paragraph testIdPrefix="generic-error" type="secondary">
          {errorMessage}
        </Typography.Paragraph>

        <Flex gap="middle">
          {customActions || (
            <>
              {showGoBack && (
                <Button
                  testIdPrefix="error-go-back"
                  onClick={onDismissError}
                  icon={<ArrowLeftOutlined />}
                >
                  {t("common.components.errors_actions_goBack")}
                </Button>
              )}
              {showGoToDashboard && (
                <Button
                  testIdPrefix="error-go-to-dashboard"
                  type="primary"
                  onClick={handleGoToAppDashboard ?? handleGoToDashboard}
                  icon={<HomeOutlined />}
                >
                  {t("common.components.errors_actions_goToDashboard")}
                </Button>
              )}
            </>
          )}
        </Flex>

        {isDevelopment && devErrorItems.length > 0 && (
          <div className={styles.devErrorInfo}>
            <div className={styles.devModeBanner}>
              <BugOutlined className={styles.devModeIcon} />
              <span>Development Mode - Debug Information</span>
              <Badge
                count="DEV"
                testIdPrefix="generic-error-dev-mode"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "10px",
                }}
              />
            </div>
            <Collapse items={devErrorItems} defaultActiveKey={["1"]} size="small" />
          </div>
        )}
      </Flex>
    </main>
  );
}
